import { RefProps } from '@lib/types'
import { DatePicker } from 'antd'
import { RangePickerProps } from 'antd/lib/date-picker'
import cx from 'classnames'
import moment from 'moment'
import React from 'react'
import styled from 'styled-components'

type Props = RangePickerProps &
  RefProps & {
    maxRange?: {
      range: number
      unit: 'day' | 'month' | 'year' | 'week'
    }
    rangeDateDisabled?: {
      start?: 'disabledAfter' | 'disabledBefore'
      end?: 'disabledBefore' | 'disabledAfter'
    }
    /**
     * Indicates whether the selected date range should cover the whole day.
     */
    wholeDay?: boolean
  }

const RangePicker = React.forwardRef<typeof DatePicker.RangePicker, Props>(
  (
    {
      className = '',
      maxRange,
      rangeDateDisabled,
      onChange,
      wholeDay = false,
      ...props
    },
    ref
  ) => {
    const [rangeDateType, setRangeDateType] = React.useState<
      'start' | 'end' | null
    >(null)

    const [value, setValue] = React.useState<{
      start: number | moment.Moment | undefined
      end: number | moment.Moment | undefined
    }>({
      start: undefined,
      end: undefined,
    })

    const rangePickerRef = React.useRef<HTMLDivElement | null>(null)

    const handleChangeDate = (val: any) => {
      const [start, end] = val ? val : []
      let _end = end
      if (maxRange) {
        const { range, unit } = maxRange
        if (start && end && moment(start).add(range, unit).isBefore(end)) {
          _end = moment(start).add(range, unit)
        }
      }
      let _start = wholeDay ? moment(start).startOf('day') : moment(start)
      _end = wholeDay ? moment(_end).endOf('day') : moment(_end)
      setValue((prev: any) => ({
        start: _start,
        end: _end,
      }))
      if (onChange) {
        onChange([_end ? _start : null, _end ? _end : null], undefined)
      }
    }

    const handleDisabledDate = (current: any) => {
      if (maxRange) {
        const { range, unit } = maxRange
        if (rangeDateType === 'start') {
          return current.year() < 2022 || current > moment().endOf('day')
        }
        return (
          current > moment().endOf('day') ||
          current > moment(value.start).add(range, unit).endOf('day')
        )
      }
      if (!rangeDateDisabled) {
        return false
      }

      const rangeDisabledType =
        rangeDateType === 'start'
          ? rangeDateDisabled.start
          : rangeDateDisabled.end

      if (rangeDisabledType === 'disabledAfter') {
        return current > moment().endOf('day')
      }
      if (rangeDisabledType === 'disabledBefore') {
        return current < moment().startOf('day')
      }
      return false
    }

    React.useEffect(() => {
      const rangeInputs =
        rangePickerRef.current?.querySelectorAll('.ant-picker-input > input') ||
        []

      const handleFocus = (index: number) => {
        setRangeDateType(index === 0 ? 'start' : 'end')
      }

      rangeInputs.forEach((input: Element, index: number) => {
        input.addEventListener('focus', () => {
          handleFocus(index)
        })
      })

      return () => {
        rangeInputs.forEach((input: Element, index: number) => {
          input.removeEventListener('focus', () => {
            handleFocus(index)
          })
        })
      }
    }, [])

    return (
      <RangeStyledPicker
        forwardRef={ref}
        className={cx('c-datepicker', className)}
        disabledDate={handleDisabledDate}
        onChange={handleChangeDate}
        onBlur={() => {
          setRangeDateType(null)
        }}
        {...props}
      />
    )
  }
)

const RangeStyledPicker = styled(DatePicker.RangePicker)`
  &.ant-picker {
    border-radius: 4px;
    background-color: ${p => p.theme.datePicker.bg};
    border-color: ${p => p.theme.datePicker.borderColor};
    &:hover,
    &:focus {
      border-color: ${p => p.theme.datePicker.hoverAndFocusBorderColor};
    }
    > .ant-picker-active-bar {
      background: ${p => p.theme.datePicker.rangeActiveBarBg};
    }
    .ant-picker-separator {
      color: ${p => p.theme.datePicker.placeholderColor};
    }
    > .ant-picker-input {
      > input {
        color: ${p => p.theme.datePicker.color};
        &::placeholder {
          color: ${p => p.theme.datePicker.placeholderColor};
        }
      }
      > input[disabled] {
        color: ${p => p.theme.datePicker.disabledColor};
      }
    }
    &.ant-picker-disabled {
      background: ${p => p.theme.datePicker.disabledBg};
      color: ${p => p.theme.datePicker.disabledColor};
    }
    > .ant-picker-clear svg[data-icon='close-circle'] {
      fill: ${p => p.theme.datePicker.clearIconFill};
    }
    > .ant-picker-suffix svg[data-icon='calendar'] {
      fill: ${p => p.theme.datePicker.calendarIconFill};
    }
    .ant-picker-clear {
      background: #000;
    }
  }
`

export default RangePicker
